import { useCallback, useEffect } from 'react'
import { t } from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { stringComparer } from '../../../utils/helpers/stringHelpers'
import { UserAppModel, getUserApps } from '../../idm/getAllUserApplications'
import {
  setLoading,
  getLoading,
  getCalled,
  getUserApps as getApps,
  getError,
  setUserApps,
  setError,
  setCalled,
  setUserReportApps,
} from '../../../store/userApps/userAppsSlice'
import api from '../..'
import {
  REACT_APP_TEAMDOCS_DASHBOARD_URL,
  REACT_APP_TEAMDOCS_OKTA_APP_ID,
} from '../../../envVariables'
import {
  getReadyState,
  getUserInfo,
} from '../../../store/userInfo/userInfoSlice'

function useApps() {
  const dispatch = useDispatch()
  const userApps = useSelector(getApps)
  const loading = useSelector(getLoading)
  const called = useSelector(getCalled)
  const error = useSelector(getError)
  const userInfo = useSelector(getUserInfo)
  const isReadyUserInfo = useSelector(getReadyState)
  const brandedAppsTool = useFeatureFlag('BrandedAppsToolsList')
  const teamDocsInternal = useFeatureFlag('TeamDocs2024Internal')
  const teamDocsExternal = useFeatureFlag('TeamDocs2024External')

  const SortAndFilterApps = (
    apps: UserAppModel[],
    appsWhitelist: string[],
    disallowedToolIds: string[],
  ): UserAppModel[] => {
    // note: we can sort/case transform both and make it fwd only for performance but not really required here
    // whitelist should be the shorter one
    let filtered = appsWhitelist.reduce(
      (values: UserAppModel[], item: string) => {
        const match = apps.filter(
          (a) => a.identifier?.toLowerCase() === item?.toLowerCase(),
        )
        if (match.length > 0) values.push(match[0])
        return values
      },
      [],
    )

    if (brandedAppsTool) {
      filtered = apps.filter(
        (item: UserAppModel) =>
          item.identifier && !disallowedToolIds.includes(item.identifier),
      )
    }

    // NOTE: This logic is intentionally commented out for now.
    // Keeping it for future reference in case we need to re-enable it later.

    // remove report tile from branded apps which are binding at navigation menu.
    // filtered = filtered.filter(
    //   (item: UserAppModel) => !item.launchUri?.toLowerCase().includes('report'),
    // )

    return filtered.sort((a: UserAppModel, b: UserAppModel) =>
      stringComparer(a.name, b.name),
    )
  }

  const getAllowedAppsList = async (): Promise<string[]> => {
    if (brandedAppsTool) {
      return Promise.resolve([])
    }
    try {
      const response = await api.user.configuration_GetAllowedToolsList()
      return response.data ?? []
    } catch {
      return Promise.resolve([])
    }
  }

  const removeLegacyTeamDocsTile = (apps: UserAppModel[]) =>
    apps.filter((app) => app.identifier !== REACT_APP_TEAMDOCS_OKTA_APP_ID)

  const getTeamDocsTile = (
    identifier: string,
    launchUri: string,
  ): UserAppModel => ({
    id: undefined,
    identifier,
    name: 'TeamDocs',
    description: t('HomePage.TeamDocsDescription'),
    category: t('HomePage.TeamDocsCategory'),
    launchUri,
    logoUri: undefined,
    brandedApplicationType: undefined,
  })

  const fetch = async (forceLoad = false) => {
    try {
      if (called && !forceLoad) return

      dispatch(setLoading(true))
      dispatch(setCalled(true))

      // parallel execution
      const [
        appsCall,
        appsWhitelist,
        teamDocsProjectSites,
        teamDocsProjects2024,
        disallowedToolIds,
      ] = await Promise.all([
        getUserApps(),
        getAllowedAppsList(),
        api.user
          .teamDocsUserMappings_GetTeamDocsUserMappings()
          .then((res) => res.data || []),
        api.user
          .teamDocs_GetTeamDocsSitesOfUser()
          .then((res) => res.data || []),
        api.user
          .adminSettings_GetByKey('REACT_APP_DISALLOWED_TOOLS')
          .then((res) => {
            const rawValue = res?.data?.value
            if (rawValue) {
              try {
                return JSON.parse(rawValue)
              } catch {
                return []
              }
            }
            return []
          }),
      ])

      const userReportApps = [...appsCall].filter((app) =>
        app.launchUri?.toLowerCase().includes('report'),
      )
      dispatch(setUserReportApps(userReportApps))

      let apps = appsCall

      if (userInfo?.userType === 'Internal User' && teamDocsInternal) {
        apps = removeLegacyTeamDocsTile(apps)
        if (teamDocsProjects2024?.length > 0) {
          apps.push(
            getTeamDocsTile(
              REACT_APP_TEAMDOCS_OKTA_APP_ID,
              REACT_APP_TEAMDOCS_DASHBOARD_URL,
            ),
          )
        }
      } else if (userInfo?.userType === 'External User') {
        apps = removeLegacyTeamDocsTile(apps)
      }

      // sort here even if data comes sorted, we do not control this source.
      const filtered = SortAndFilterApps(
        apps as UserAppModel[],
        appsWhitelist as string[],
        disallowedToolIds as string[],
      )

      if (userInfo?.userType === 'External User') {
        if (
          (teamDocsExternal && teamDocsProjects2024?.length > 0) ||
          teamDocsProjectSites?.length > 0
        ) {
          filtered.push(getTeamDocsTile('teamdocs', '/projects'))
        }
      }

      filtered.sort((a, b) => stringComparer(a.name, b.name))

      if (!brandedAppsTool) {
        filtered.push({
          id: undefined,
          identifier: 'moretools',
          name: t('HomePage.AreYouMissingTools'),
          description: t('HomePage.RequestMissingTools'),
          category: t('HomePage.AreYouMissingToolsCategory'),
          launchUri: '/support',
          logoUri: undefined,
          brandedApplicationType: undefined,
        })
      }

      dispatch(setLoading(false))
      dispatch(setUserApps(filtered || []))
    } catch (err) {
      dispatch(setLoading(false))
      dispatch(setUserApps([]))
      dispatch(setError(err as Error))
    }
  }

  useEffect(() => {
    if (isReadyUserInfo) {
      localStorage.removeItem('loopCounter')
      fetch()
    }
  }, [isReadyUserInfo])

  const refetch = useCallback(() => fetch(true), [])

  return { userApps, loading, error, refetch }
}

export default useApps
